import {
  Component,
  Inject,
  OnDestroy,
  OnInit
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import {
  TaxReturnTypeService,
  TaxReturnType,
  SmartFormTypeService,
  SmartFormType,
  Source,
  SourceService
} from "../../../../../../swaggerServices";
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl,
  FormControl
} from "@angular/forms";
import { ConfigStateService } from "../../../../config/config-state.service";
import { TenantConfigService } from "../../../../../shared/services/tenant-config.service";
import { environment } from "../../../../../environments/environment";
import { TranslocoService } from "@jsverse/transloco";
import { IFormField } from "../../../../../shared/components/form-field-wrapper/form-field-wrapper.component";
import { DropdownListOption } from "../../../../../shared/components/search-dropdown/search-dropdown.component";
import { typeToDropdownListOptions } from "../../../../../shared/util/tax-return-type-subtype";

interface AddUpdateSmartFormTypeDialogData {
  smartFormType: SmartFormType;
  configYear: number;
}

@Component({
    selector: "app-add-update-smart-form-type",
    templateUrl: "./add-update-smart-form-type.component.html",
    styleUrls: ["./add-update-smart-form-type.component.scss"],
    standalone: false
})
export class AddUpdateSmartFormTypeComponent implements OnInit, OnDestroy {
  smartFormType: SmartFormType;
  configYear: number;
  question: IFormField = {
    id: 1,
    answers: [{ answer: "yes" }, { answer: "no" }]
  };
  form: UntypedFormGroup;
  isCreate: boolean = false;
  isProductionMode: boolean;
  taxReturnTypeList: DropdownListOption[] = [];
  sourceListOptions: DropdownListOption[] = [];
  subscriptions: Subscription[] = [];
  smartFormAlias: string;
  smartFormHasQuestions: boolean;
  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private config: ConfigStateService,
    private taxReturnTypeService: TaxReturnTypeService,
    private smartFormTypeService: SmartFormTypeService,
    private sourceService: SourceService,
    private translocoService: TranslocoService,
    private dialogRef: MatDialogRef<AddUpdateSmartFormTypeComponent>,
    @Inject(MAT_DIALOG_DATA) data: AddUpdateSmartFormTypeDialogData
  ) {
    this.smartFormType = data.smartFormType;
    this.configYear = data.configYear;
    this.isProductionMode = environment.production;
  }

  get name(): AbstractControl {
    return this.form.get("name");
  }

  get description(): AbstractControl {
    return this.form.get("description");
  }

  get taxReturnTypeName(): AbstractControl {
    return this.form.get("taxReturnTypeName");
  }

  get exportable(): AbstractControl {
    return this.form.get("exportable");
  }

  get source(): AbstractControl {
    return this.form.get("source");
  }

  get waitForAllSmartFormsInBatch(): AbstractControl {
    return this.form.get("waitForAllSmartFormsInBatch");
  }

  ngOnInit() {
    this.isCreate = !this.smartFormType;
    this.initForm();
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        const uiConfig = new TenantConfigService(config);
        this.smartFormAlias = uiConfig.config.smartFormAlias;
        this.translocoService
          .selectTranslate("smart-form-alias")
          .subscribe(value => (this.smartFormAlias = value));
      }),

      this.taxReturnTypeService.getTaxReturnTypes().subscribe(types => {
        let i = 0;
        this.taxReturnTypeList = types.map(val =>
          typeToDropdownListOptions(val, i++)
        );
      }),

      this.sourceService.getSources().subscribe(sources => {
        this.sourceListOptions = sources
          .filter(source => source.type === "oneteam")
          .map(source => ({
            id: source.id,
            name: source.name,
            value: source.id.toString()
          }));
      })
    );
    this.validateSourceEditingEnabled();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  validateSourceEditingEnabled() {
    if (this.smartFormType && this.smartFormType.id) {
      this.subscriptions.push(
        this.smartFormTypeService
          .getSmartFormQuestionsById(this.smartFormType.id)
          .subscribe(result => {
            if (result) {
              this.smartFormHasQuestions = result;
            }
          })
      );
    }
  }

  saveSmartFormType() {
    //if the smart form is exportable to OneTeam, user can set waitForAllSmartFormsInBatch, and waitForAllSmartFormsInBatch isn't editable
    if (this.isCreate) {
      this.waitForAllSmartFormsInBatch.setValue(
        this.exportable.value ? this.waitForAllSmartFormsInBatch.value : false
      );
    }
    let postData = {
      id: undefined,
      name: this.name.value,
      description: this.description.value,
      sourceId: this.source.value.id,
      exportable: this.exportable.value ? true : false,
      taxReturnTypeId: this.taxReturnTypeName.value.id,
      waitForAllSmartFormsInBatch: this.waitForAllSmartFormsInBatch.value,
      year: this.configYear
    };

    if (this.isCreate) {
      this.smartFormTypeService
        .addSmartFormType(postData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          result => {
            this.dialogRef.close(true);
          },
          error => {
            this.dialogRef.close(false);
          }
        );
    } else {
      postData.id = this.smartFormType.id;
      this.smartFormTypeService
        .updateSmartFormType(postData)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          result => {
            this.dialogRef.close(true);
          },
          error => {
            this.dialogRef.close(false);
          }
        );
    }
  }

  onClose() {
    this.dialogRef.close(false);
  }

  private initForm() {
    this.form = this.formBuilder.group({
      name: ["", Validators.required],
      description: ["", Validators.maxLength(250)],
      exportable: [""],
      taxReturnTypeName: ["", Validators.required],
      source: ["", Validators.required],
      waitForAllSmartFormsInBatch: [""]
    });

    if (!this.isCreate) {
      this.name.setValue(this.smartFormType.name);
      this.description.setValue(this.smartFormType.description);
      this.exportable.setValue(this.smartFormType.exportable);
      this.taxReturnTypeName.setValue(this.smartFormType.taxReturnTypeId);
      this.source.setValue(this.smartFormType.sourceId);
      this.waitForAllSmartFormsInBatch.setValue(
        this.smartFormType.waitForAllSmartFormsInBatch
      );
    }
  }
}
